// src/components/ClientListView.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { ROUTES } from '../constants/routes';
import HealthScoreSparkline from './HealthScoreSparkline';
import { RiAddLine, RiEditLine } from 'react-icons/ri';

// Chevron icon component for expand/collapse
const ChevronIcon = ({ expanded }) => (
    <svg 
        className={`h-5 w-5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        xmlns="http://www.w3.org/2000/svg" 
        viewBox="0 0 20 20" 
        fill="currentColor"
    >
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);

// Expanded detail row component
const AccountDetailDropdown = ({ client, loading, detailData, onNavigate }) => {
    if (loading) {
        return (
            <tr>
                <td colSpan="8" className="px-6 py-4" style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}>
                    <div className="flex items-center justify-center py-4">
                        <div 
                            className="animate-spin rounded-full h-5 w-5 border-b-2 mr-2"
                            style={{ borderColor: 'var(--pallium-primary-purple)' }}
                        ></div>
                        <span style={{ color: 'var(--pallium-text-secondary)' }}>Loading details...</span>
                    </div>
                </td>
            </tr>
        );
    }

    if (!detailData) {
        return null;
    }

    const { goalsAtRisk = [], criticalIssues = [], priorityCounts = {} } = detailData;
    const clientPath = `${ROUTES.ACCOUNTS}/${client.clientId || client.name}`;

    return (
        <tr>
            <td colSpan="8" className="p-0">
                <div 
                    className="px-6 py-4"
                    style={{ 
                        backgroundColor: 'var(--pallium-purple-0-5)',
                        borderBottom: '2px solid var(--pallium-purple-2)'
                    }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Priority Alerts Section */}
                        <div>
                            <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--pallium-primary-purple)' }}>
                                Priority Alerts
                            </h4>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span style={{ color: 'var(--pallium-text-secondary)' }}>Critical Issues</span>
                                    <span 
                                        className="font-medium"
                                        style={{ color: priorityCounts.criticalIssues > 0 ? 'var(--pallium-error-red)' : 'var(--pallium-text-primary)' }}
                                    >
                                        {priorityCounts.criticalIssues || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span style={{ color: 'var(--pallium-text-secondary)' }}>Blocked Initiatives</span>
                                    <span 
                                        className="font-medium"
                                        style={{ color: priorityCounts.blockedInitiatives > 0 ? 'var(--pallium-warning-yellow)' : 'var(--pallium-text-primary)' }}
                                    >
                                        {priorityCounts.blockedInitiatives || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span style={{ color: 'var(--pallium-text-secondary)' }}>Overdue Actions</span>
                                    <span 
                                        className="font-medium"
                                        style={{ color: priorityCounts.overdueActions > 0 ? 'var(--pallium-error-red)' : 'var(--pallium-text-primary)' }}
                                    >
                                        {priorityCounts.overdueActions || 0}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span style={{ color: 'var(--pallium-text-secondary)' }}>Neglected Goals</span>
                                    <span 
                                        className="font-medium"
                                        style={{ color: priorityCounts.neglectedGoals > 0 ? 'var(--pallium-warning-yellow)' : 'var(--pallium-text-primary)' }}
                                    >
                                        {priorityCounts.neglectedGoals || 0}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Goals at Risk Section */}
                        <div>
                            <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--pallium-primary-purple)' }}>
                                Goals at Risk ({goalsAtRisk.length})
                            </h4>
                            {goalsAtRisk.length > 0 ? (
                                <ul className="space-y-2">
                                    {goalsAtRisk.slice(0, 3).map((goal, idx) => (
                                        <li key={goal.goalId || idx} className="text-sm">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onNavigate(`${clientPath}/goals/${goal.goalId}`);
                                                }}
                                                className="text-left hover:underline truncate max-w-full block"
                                                style={{ color: 'var(--pallium-primary-purple)' }}
                                                title={goal.goalName}
                                            >
                                                {goal.goalName}
                                            </button>
                                        </li>
                                    ))}
                                    {goalsAtRisk.length > 3 && (
                                        <li className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
                                            +{goalsAtRisk.length - 3} more
                                        </li>
                                    )}
                                </ul>
                            ) : (
                                <p className="text-sm" style={{ color: 'var(--pallium-text-secondary)', fontStyle: 'italic' }}>
                                    No goals at risk
                                </p>
                            )}
                        </div>

                        {/* Critical Issues Section */}
                        <div>
                            <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--pallium-primary-purple)' }}>
                                Critical Issues ({criticalIssues.length})
                            </h4>
                            {criticalIssues.length > 0 ? (
                                <ul className="space-y-2">
                                    {criticalIssues.slice(0, 3).map((issue, idx) => (
                                        <li key={issue.issueId || idx} className="text-sm">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onNavigate(`${clientPath}/issues/${issue.issueId}`);
                                                }}
                                                className="text-left hover:underline truncate max-w-full block"
                                                style={{ color: 'var(--pallium-error-red)' }}
                                                title={issue.issueName}
                                            >
                                                {issue.issueName}
                                            </button>
                                        </li>
                                    ))}
                                    {criticalIssues.length > 3 && (
                                        <li className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>
                                            +{criticalIssues.length - 3} more
                                        </li>
                                    )}
                                </ul>
                            ) : (
                                <p className="text-sm" style={{ color: 'var(--pallium-text-secondary)', fontStyle: 'italic' }}>
                                    No critical issues
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </td>
        </tr>
    );
};

// Create Client Modal
const CreateClientModal = ({ isOpen, onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setName('');
            setDomain('');
            setError(null);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !domain.trim()) {
            setError('Name and domain are required');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await apiService.createClient({ name: name.trim(), domain: domain.trim() });
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create client');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-xl p-6 w-full max-w-md" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}>
                <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--pallium-text-primary)' }}>Add New Account</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Account Name <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter account name..."
                            className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                            style={{ border: '1px solid var(--pallium-stroke-light)' }}
                            required
                            autoFocus
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Domain <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="e.g., example.com"
                            className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                            style={{ border: '1px solid var(--pallium-stroke-light)' }}
                            required
                        />
                    </div>
                    {error && (
                        <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}>
                            {error}
                        </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg" style={{ color: 'var(--pallium-text-secondary)', backgroundColor: 'var(--pallium-bg-secondary)' }} disabled={isLoading}>
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 text-sm font-medium rounded-lg text-white" style={{ backgroundColor: isLoading ? 'var(--pallium-icon-gray)' : 'var(--pallium-primary-purple)' }} disabled={isLoading}>
                            {isLoading ? 'Creating...' : 'Add Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const EditClientModal = ({ isOpen, onClose, onSuccess, client, isLoading }) => {
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setName(client?.name || '');
            setDomain(client?.domain || '');
            setError(null);
        }
    }, [isOpen, client]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!client?.clientId) {
            setError('Client ID is missing.');
            return;
        }
        if (!name.trim() || !domain.trim()) {
            setError('Name and domain are required');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await apiService.updateClient(client.clientId, {
                name: name.trim(),
                domain: domain.trim(),
            });
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to update client');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-xl p-6 w-full max-w-md" style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-lg)' }}>
                <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--pallium-text-primary)' }}>Edit Account</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Account Name <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter account name..."
                            className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                            style={{ border: '1px solid var(--pallium-stroke-light)' }}
                            required
                            autoFocus
                            disabled={isLoading}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                            Domain <span style={{ color: 'var(--pallium-error-red)' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="e.g., example.com"
                            className="w-full px-3 py-2 text-sm rounded-lg focus:outline-none focus:ring-2"
                            style={{ border: '1px solid var(--pallium-stroke-light)' }}
                            required
                            disabled={isLoading}
                        />
                    </div>
                    {error && (
                        <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}>
                            {error}
                        </div>
                    )}
                    <div className="flex justify-end gap-3 mt-6">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg" style={{ color: 'var(--pallium-text-secondary)', backgroundColor: 'var(--pallium-bg-secondary)' }} disabled={saving || isLoading}>
                            Cancel
                        </button>
                        <button type="submit" className="px-4 py-2 text-sm font-medium rounded-lg text-white" style={{ backgroundColor: saving ? 'var(--pallium-icon-gray)' : 'var(--pallium-primary-purple)' }} disabled={saving || isLoading}>
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Health filter options
const HEALTH_FILTER_OPTIONS = [
    { value: '', label: 'All Health Scores' },
    { value: '0-20', label: '0-20%' },
    { value: '21-40', label: '21-40%' },
    { value: '41-60', label: '41-60%' },
    { value: '61-80', label: '61-80%' },
    { value: '81-100', label: '81-100%' }
];

// Column definitions for sorting
const SORTABLE_COLUMNS = [
    { key: 'expand', label: '', align: 'center', sortable: false, width: '48px' },
    { key: 'name', label: 'Account Name', align: 'left' },
    { key: 'goalCount', label: 'Goals', align: 'center' },
    { key: 'actionItemCount', label: 'Action Items', align: 'center' },
    { key: 'blockerCount', label: 'Issues', align: 'center' },
    { key: 'initiativeCount', label: 'Initiatives', align: 'center' },
    { key: 'healthScore', label: 'Health Score', align: 'center' },
    { key: 'trend', label: 'Trend', align: 'center', sortable: false },
    { key: 'actions', label: 'Actions', align: 'center', sortable: false }
];

const ClientListView = () => {
    const [clientsData, setClientsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [healthFilter, setHealthFilter] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [detailDataCache, setDetailDataCache] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const loadingDetailsRef = useRef({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState(null);
    const navigate = useNavigate();

    // Toggle row expansion and fetch details if needed
    const toggleRowExpansion = useCallback(async (clientId, e) => {
        e.stopPropagation();
        
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(clientId)) {
                newSet.delete(clientId);
            } else {
                newSet.add(clientId);
            }
            return newSet;
        });

        // Fetch detail data if not already cached and not currently loading
        if (!detailDataCache[clientId] && !loadingDetailsRef.current[clientId]) {
            // Update both state (for UI) and ref (for immediate duplicate prevention)
            setLoadingDetails(prev => ({ ...prev, [clientId]: true }));
            loadingDetailsRef.current[clientId] = true;
            
            try {
                const overviewData = await apiService.getClientOverview(clientId);
                setDetailDataCache(prev => ({
                    ...prev,
                    [clientId]: {
                        goalsAtRisk: overviewData.goalsAtRisk || [],
                        criticalIssues: overviewData.criticalIssues || [],
                        priorityCounts: overviewData.priorityCounts || {}
                    }
                }));
            } catch (err) {
                console.warn(`Failed to fetch details for ${clientId}:`, err);
                setDetailDataCache(prev => ({
                    ...prev,
                    [clientId]: { goalsAtRisk: [], criticalIssues: [], priorityCounts: {} }
                }));
            } finally {
                setLoadingDetails(prev => ({ ...prev, [clientId]: false }));
                loadingDetailsRef.current[clientId] = false;
            }
        }
    }, [detailDataCache]);

    const handleEditClient = useCallback(async (client, e) => {
        if (e) e.stopPropagation();
        const clientId = client.clientId || client.name;
        if (!clientId) return;
        setEditLoading(true);
        setEditError(null);
        try {
            const details = await apiService.getClientById(clientId);
            setEditingClient({
                ...client,
                ...details,
                clientId: details?.clientId || clientId,
            });
            setShowEditModal(true);
        } catch (err) {
            console.error('Failed to load client details:', err);
            setEditError(err.message || 'Failed to load client details.');
        } finally {
            setEditLoading(false);
        }
    }, []);

    const fetchClientsData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiService.getAllClientsWithStats();
            
            // Fetch health trends for each client
            const clientsWithHealth = await Promise.all(result.map(async (record) => {
                let healthTrend = [];
                try {
                    // Use clientId if available, fallback to name for backwards compatibility
                    const clientIdentifier = record.clientId || record.name;
                    healthTrend = await apiService.getClientHealthTrend(clientIdentifier, 90);
                } catch (err) {
                    console.warn(`Failed to fetch health trend for ${record.name}:`, err);
                }
                
                return {
                    clientId: record.clientId,  // Store clientId for routing
                    name: record.name,
                    goalCount: record.goalCount,
                    actionItemCount: record.actionItemCount,
                    blockerCount: record.blockerCount,
                    initiativeCount: record.initiativeCount,
                    healthScore: record.healthScore,
                    healthDate: record.healthDate,
                    healthTrend: healthTrend
                };
            }));
            
            setClientsData(clientsWithHealth);
        } catch (err) {
            console.error("Error fetching client list with stats:", err);
            setError("Failed to load account data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClientsData();
    }, [fetchClientsData]);

    // Handle column header click for sorting (ternary: desc -> asc -> none)
    const handleSort = (columnKey) => {
        const column = SORTABLE_COLUMNS.find(col => col.key === columnKey);
        if (column?.sortable === false) return;
        
        if (sortKey === columnKey) {
            // Cycle through: desc -> asc -> none
            if (sortDirection === 'desc') {
                setSortDirection('asc');
            } else if (sortDirection === 'asc') {
                // Deselect - clear sorting
                setSortKey(null);
                setSortDirection(null);
            }
        } else {
            // New column, default to descending
            setSortKey(columnKey);
            setSortDirection('desc');
        }
    };

    // Filter and sort clients based on search term, health filter, and sort settings
    const filteredClients = useMemo(() => {
        // First filter
        const filtered = clientsData.filter(client => {
            // Search filter
            const matchesSearch = !searchTerm || 
                client.name?.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Health filter
            let matchesHealth = true;
            if (healthFilter && client.healthScore !== null && client.healthScore !== undefined) {
                const [min, max] = healthFilter.split('-').map(Number);
                matchesHealth = client.healthScore >= min && client.healthScore <= max;
            } else if (healthFilter && (client.healthScore === null || client.healthScore === undefined)) {
                matchesHealth = false;
            }
            
            return matchesSearch && matchesHealth;
        });

        // If no sort is selected, return filtered array as-is
        if (!sortKey || !sortDirection) {
            return filtered;
        }

        // Then sort
        return [...filtered].sort((a, b) => {
            let aValue = a[sortKey];
            let bValue = b[sortKey];

            // Handle null/undefined values - push them to the end
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            // String comparison for name
            if (sortKey === 'name') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }

            // Numeric comparison for counts and health score
            const diff = aValue - bValue;
            return sortDirection === 'asc' ? diff : -diff;
        });
    }, [clientsData, searchTerm, healthFilter, sortKey, sortDirection]);

    // Get health score color styles
    const getHealthScoreStyle = (healthScore) => {
        if (healthScore === null || healthScore === undefined) {
            return { color: 'var(--pallium-icon-gray)', fontStyle: 'italic' };
        }
        if (healthScore > 70) {
            return { color: 'var(--pallium-success-green)', fontWeight: 600 };
        } else if (healthScore >= 40) {
            return { color: 'var(--pallium-warning-yellow)', fontWeight: 600 };
        } else {
            return { color: 'var(--pallium-error-red)', fontWeight: 600 };
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div 
                    className="animate-spin rounded-full h-12 w-12 border-b-2"
                    style={{ borderColor: 'var(--pallium-primary-purple)' }}
                ></div>
                <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Loading account data...
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="p-4 rounded-md"
                style={{ 
                    backgroundColor: 'var(--pallium-alert-error-bg)', 
                    color: 'var(--pallium-error-red)' 
                }}
            >
                <strong>Error:</strong> {error} 
                <button 
                    onClick={fetchClientsData} 
                    className="ml-2 text-white font-bold py-1 px-2 rounded text-sm transition-colors duration-200"
                    style={{ backgroundColor: 'var(--pallium-error-red)' }}
                    onMouseOver={(e) => e.target.style.opacity = '0.8'}
                    onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (clientsData.length === 0) {
        return (
            <div className="p-4 text-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                No clients found.
            </div>
        );
    }

    return (
        <div className="container mx-auto">
            {/* Search, Filter Row, and Add Button */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                {/* Search Bar */}
                <div className="relative w-full sm:w-64">
                    <input
                        type="text"
                        placeholder="Search accounts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 focus:outline-none"
                        style={{ 
                            border: '1px solid var(--pallium-border-default)',
                            borderRadius: 'var(--pallium-border-radius-md)'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--pallium-border-focus)';
                            e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--pallium-border-default)';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                    <svg 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" 
                        style={{ color: 'var(--pallium-icon-gray)' }}
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                    >
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                </div>
                
                <div className="flex items-center gap-3">
                    {/* Health Filter */}
                    <select
                        value={healthFilter}
                        onChange={(e) => setHealthFilter(e.target.value)}
                        className="px-4 py-2 focus:outline-none cursor-pointer"
                        style={{ 
                            border: '1px solid var(--pallium-border-default)',
                            borderRadius: 'var(--pallium-border-radius-md)',
                            color: 'var(--pallium-text-primary)'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--pallium-border-focus)';
                            e.target.style.boxShadow = 'var(--pallium-focus-ring-primary)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--pallium-border-default)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        {HEALTH_FILTER_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>

                    {/* Add Account Button */}
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white"
                        style={{ backgroundColor: 'var(--pallium-primary-purple)' }}
                    >
                        <RiAddLine className="w-4 h-4" />
                        Add Account
                    </button>
                </div>
            </div>

            {editError && (
                <div
                    className="mb-4 p-3 rounded-lg text-sm"
                    style={{ backgroundColor: 'var(--pallium-alert-error-bg)', color: 'var(--pallium-error-red)' }}
                >
                    {editError}
                </div>
            )}

            {/* Table */}
            <div 
                className="rounded-xl overflow-hidden"
                style={{ 
                    backgroundColor: 'var(--pallium-white)',
                    boxShadow: 'var(--pallium-shadow-md)'
                }}
                data-testid="client-list"
            >
                <table className="min-w-full leading-normal">
                    <thead>
                        <tr style={{ backgroundColor: 'var(--pallium-purple-0-5)' }}>
                            {SORTABLE_COLUMNS.map((column) => {
                                const isSortable = column.sortable !== false;
                                const isActive = sortKey === column.key;
                                
                                return (
                                    <th 
                                        key={column.key}
                                        className={`px-6 py-4 text-${column.align} text-xs font-semibold uppercase tracking-wider ${isSortable ? 'cursor-pointer select-none' : ''}`}
                                        style={{ 
                                            color: 'var(--pallium-primary-purple)',
                                            borderBottom: isActive ? '2px solid var(--pallium-primary-purple)' : '2px solid var(--pallium-purple-2)',
                                            transition: 'border-color 0.2s ease'
                                        }}
                                        onClick={() => isSortable && handleSort(column.key)}
                                        onMouseOver={(e) => {
                                            if (isSortable) {
                                                e.currentTarget.style.backgroundColor = 'var(--pallium-purple-1)';
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <div className={`flex items-center gap-1 ${column.align === 'center' ? 'justify-center' : ''}`}>
                                            <span>{column.label}</span>
                                            {isSortable && (
                                                <span 
                                                    className="inline-flex items-center ml-1"
                                                    style={{ 
                                                        width: '10px',
                                                        height: '12px',
                                                        transition: 'opacity 0.2s ease'
                                                    }}
                                                >
                                                    {/* Show only the active arrow, or a neutral indicator when not sorted */}
                                                    {isActive && sortDirection === 'asc' && (
                                                        <svg 
                                                            width="10" 
                                                            height="6" 
                                                            viewBox="0 0 10 6" 
                                                            fill="currentColor"
                                                        >
                                                            <path d="M5 0L10 6H0L5 0Z" />
                                                        </svg>
                                                    )}
                                                    {isActive && sortDirection === 'desc' && (
                                                        <svg 
                                                            width="10" 
                                                            height="6" 
                                                            viewBox="0 0 10 6" 
                                                            fill="currentColor"
                                                        >
                                                            <path d="M5 6L0 0H10L5 6Z" />
                                                        </svg>
                                                    )}
                                                    {/* Neutral sort icon when column is not active */}
                                                    {!isActive && (
                                                        <svg 
                                                            width="10" 
                                                            height="12" 
                                                            viewBox="0 0 10 12" 
                                                            fill="currentColor"
                                                            style={{ opacity: 0.3 }}
                                                        >
                                                            <path d="M5 0L8 4H2L5 0Z" />
                                                            <path d="M5 12L2 8H8L5 12Z" />
                                                        </svg>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClients.map((client) => {
                            const clientKey = client.clientId || client.name;
                            const isExpanded = expandedRows.has(clientKey);
                            
                            return (
                                <React.Fragment key={clientKey}>
                                    <tr 
                                        className="cursor-pointer transition-colors duration-200"
                                        style={{ 
                                            borderBottom: isExpanded ? 'none' : '1px solid var(--pallium-stroke-light)',
                                            backgroundColor: isExpanded ? 'var(--pallium-purple-0-5)' : 'transparent'
                                        }}
                                        onClick={() => navigate(`${ROUTES.ACCOUNTS}/${clientKey}/overview`)}
                                        onMouseOver={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'; }}
                                        onMouseOut={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        data-testid="client-row"
                                    >
                                        <td className="px-3 py-4 text-center" style={{ width: '48px' }}>
                                            <button
                                                onClick={(e) => toggleRowExpansion(clientKey, e)}
                                                className="p-1 rounded-md transition-colors duration-200 hover:bg-opacity-50"
                                                style={{ color: 'var(--pallium-primary-purple)' }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-1)'}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                title={isExpanded ? 'Collapse details' : 'Expand details'}
                                                aria-expanded={isExpanded}
                                                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${client.name}`}
                                            >
                                                <ChevronIcon expanded={isExpanded} />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Link 
                                                to={`${ROUTES.ACCOUNTS}/${clientKey}/overview`} 
                                                className="font-medium hover:underline"
                                                style={{ color: 'var(--pallium-primary-purple)' }}
                                                onClick={(e) => e.stopPropagation()}
                                                data-testid="client-name"
                                            >
                                                {client.name}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 text-center" style={{ color: 'var(--pallium-text-primary)' }} data-testid="goal-count">
                                            {client.goalCount}
                                        </td>
                                        <td className="px-6 py-4 text-center" style={{ color: 'var(--pallium-text-primary)' }} data-testid="action-item-count">
                                            {client.actionItemCount}
                                        </td>
                                        <td className="px-6 py-4 text-center" style={{ color: 'var(--pallium-text-primary)' }} data-testid="blocker-count">
                                            {client.blockerCount}
                                        </td>
                                        <td className="px-6 py-4 text-center" style={{ color: 'var(--pallium-text-primary)' }} data-testid="initiative-count">
                                            {client.initiativeCount}
                                        </td>
                                        <td className="px-6 py-4 text-center" style={getHealthScoreStyle(client.healthScore)} data-testid="health-score">
                                            {client.healthScore !== null && client.healthScore !== undefined 
                                                ? `${client.healthScore.toFixed(1)}%` 
                                                : 'No data'
                                            }
                                        </td>
                                        <td className="px-6 py-4 text-center" data-testid="trend-line">
                                            {client.healthTrend && client.healthTrend.length > 0 ? (
                                                <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
                                                    <HealthScoreSparkline data={client.healthTrend} width={100} height={40} />
                                                </div>
                                            ) : (
                                                <span style={{ color: 'var(--pallium-icon-gray)', fontStyle: 'italic' }}>
                                                    No data
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={(e) => handleEditClient(client, e)}
                                                className="p-2 rounded-lg transition-colors"
                                                style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                                                title="Edit account"
                                            >
                                                <RiEditLine className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <AccountDetailDropdown
                                            client={client}
                                            loading={loadingDetails[clientKey]}
                                            detailData={detailDataCache[clientKey]}
                                            onNavigate={navigate}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                
                {/* Empty state for filtered results */}
                {filteredClients.length === 0 && (
                    <div className="p-8 text-center" style={{ color: 'var(--pallium-text-secondary)' }}>
                        No accounts match your search criteria.
                    </div>
                )}
            </div>

            {/* Create Client Modal */}
            <CreateClientModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={fetchClientsData}
            />

            {/* Edit Client Modal */}
            <EditClientModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingClient(null);
                }}
                onSuccess={fetchClientsData}
                client={editingClient}
                isLoading={editLoading}
            />
        </div>
    );
};

export default ClientListView;
